import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import TagEditor from '../Widgets/TagEditor';
import TagsDropdown from '../TagsDropdown';

const ActionsSearchAndInsertImageIntoPdf = ({ caseid, tags, settingId, onSaveStatusChange, initialScope }) => {
  const { t } = useTranslation();
  const defaultSourceMode = initialScope === 'project' ? 'other' : 'current';
  const [sourceMode, setSourceMode] = useState(defaultSourceMode);
  const [sourcePdfPath, setSourcePdfPath] = useState('');
  const [imagePath, setImagePath] = useState('');
  const [request, setRequest] = useState('');
  const [targetPath, setTargetPath] = useState('');
  const [maxZones, setMaxZones] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');
  const [filePathError, setFilePathError] = useState('');
  const [imagePathError, setImagePathError] = useState('');
  const [requestError, setRequestError] = useState('');
  const [maxZonesError, setMaxZonesError] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const sourcePdfRef = useRef(null);
  const imagePathRef = useRef(null);
  const requestRef = useRef(null);
  const targetPathRef = useRef(null);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const { handleBrowseDirectory: handleBrowseSourcePdf, FolderBrowserComponent: SourcePdfBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (selectedPath) => {
      if (selectedPath) {
        setSourcePdfPath(selectedPath);
      }
    },
  });

  const { handleBrowseDirectory: handleBrowseImagePath, FolderBrowserComponent: ImageBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (selectedPath) => {
      if (selectedPath) {
        setImagePath(selectedPath);
      }
    },
  });

  const { handleBrowseDirectory: handleBrowseTargetPath, FolderBrowserComponent: TargetBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: false,
    onFolderSelected: (selectedPath) => {
      if (selectedPath) {
        const normalizedPath = String(selectedPath).replace(/[\\/]+$/, '');
        const separator = normalizedPath.includes('\\') ? '\\' : '/';
        setTargetPath(`${normalizedPath}${separator}{filename}_search_insert.pdf`);
      }
    },
  });

  const customDataTags = useMemo(() => [], []);

  const actionSettings = useActionSettings({
    settingId,
    caseid,
    actionType: 'searchandinsertimagetopdf',
    onSaveStatusChange,
    customComparison: (initialData) => {
      if (!initialData) return false;
      const initialSourcePath = String(initialData.sourceFilePath || initialData.sourcePdfPath || '');
      const sourcePathMatches = sourceMode === 'other'
        ? initialSourcePath === sourcePdfPath
        : true;
      return (
        String(initialData.sourceMode || 'current') === sourceMode
        && sourcePathMatches
        && String(initialData.imagePath || '') === imagePath
        && String(initialData.targetPath || '') === targetPath
        && String(initialData.request || initialData.searchRequest || '') === request
        && String(initialData.maxZones || initialData.max_zones || '') === maxZones
      );
    },
    customSaveData: () => ({
      sourceMode,
      sourceFilePath: sourceMode === 'other' ? sourcePdfPath.trim() : '',
      imagePath: imagePath.trim(),
      targetPath: targetPath.trim(),
      request: request.trim(),
      maxZones: maxZones.trim(),
    }),
    loadDataFunction: (data) => {
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (error) {
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }

      setSourceMode(String(parsedData.sourceMode || defaultSourceMode));
      setSourcePdfPath(String(parsedData.sourceFilePath || parsedData.sourcePdfPath || ''));
      setImagePath(parsedData.imagePath || parsedData.sourceImagePath || '');
      setTargetPath(parsedData.targetPath || parsedData.target || '');
        setRequest(String(parsedData.request || parsedData.searchRequest || parsedData.prompt || parsedData.question || '').trim());
        setMaxZones(String(parsedData.maxZones || parsedData.max_zones || '').trim());
        setAdvancedOpen(Boolean(String(parsedData.maxZones || parsedData.max_zones || '').trim()));
    },
    enableConditions: true,
  });

  const handleMenuOpen = useCallback((event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setMenuType('');
  }, []);

  const handleTagSelect = useCallback((value) => {
    if (menuType === 'sourcePdf') {
      sourcePdfRef.current?.insertTag(value);
    } else if (menuType === 'image') {
      imagePathRef.current?.insertTag(value);
    } else if (menuType === 'request') {
      requestRef.current?.insertTag(value);
    } else if (menuType === 'target') {
      targetPathRef.current?.insertTag(value);
    }
    handleMenuClose();
  }, [handleMenuClose, menuType]);

  const handleSourceModeChange = useCallback((event) => {
    const nextMode = event.target.value;
    setSourceMode(nextMode);
    if (nextMode !== 'other') {
      setSourcePdfPath('');
      setFilePathError('');
    }
  }, []);

  const handleBrowseSourcePdfClick = useCallback(() => {
    handleBrowseSourcePdf();
  }, [handleBrowseSourcePdf]);

  const handleBrowseImageClick = useCallback(() => {
    handleBrowseImagePath();
  }, [handleBrowseImagePath]);

  const handleBrowseTargetClick = useCallback(() => {
    handleBrowseTargetPath();
  }, [handleBrowseTargetPath]);

  const handleCustomSave = useCallback(() => {
    let hasError = false;

    if (sourceMode === 'other' && !sourcePdfPath.trim()) {
      setFilePathError('A source PDF path is required.');
      hasError = true;
    } else if (filePathError) {
      setFilePathError('');
    }

    if (!imagePath.trim()) {
      setImagePathError('An image path is required.');
      hasError = true;
    } else if (imagePathError) {
      setImagePathError('');
    }

    if (!request.trim()) {
      setRequestError('A search request is required.');
      hasError = true;
    } else if (requestError) {
      setRequestError('');
    }

    const normalizedMaxZones = maxZones.trim();
    if (normalizedMaxZones) {
      const parsedMaxZones = Number(normalizedMaxZones);
      if (!Number.isInteger(parsedMaxZones) || parsedMaxZones <= 0) {
        setMaxZonesError('Max zones must be a positive integer or blank.');
        hasError = true;
      } else if (maxZonesError) {
        setMaxZonesError('');
      }
    } else if (maxZonesError) {
      setMaxZonesError('');
    }

    if (!hasError) {
      actionSettings.handleSave();
    }
  }, [actionSettings, filePathError, imagePath, imagePathError, maxZones, maxZonesError, request, requestError, sourceMode, sourcePdfPath]);

  return (
    <ActionSettingsLayout
      title={t('workflow.search_and_insert_image_into_pdf.title', 'Search Zone and Insert Image into PDF')}
      isEnabled={actionSettings.isEnabled}
      onEnabledChange={actionSettings.setIsEnabled}
      isSaveDisabled={actionSettings.isSaveDisabled}
      onSave={handleCustomSave}
      onForceRun={actionSettings.handleForceRun}
      forceRunLoading={actionSettings.forceRunLoading}
      conditions={actionSettings.conditions}
      onConditionsChange={actionSettings.handleConditionsChange}
      popUpOpen={actionSettings.popUpOpen}
      forceRunResult={actionSettings.forceRunResult}
      onCloseStatusWindow={actionSettings.handleCloseStatusWindow}
      forceRunWarningOpen={actionSettings.forceRunWarningOpen}
      forceRunWarningCount={actionSettings.forceRunWarningCount}
      onForceRunConfirm={actionSettings.handleForceRunConfirm}
      onForceRunCancel={actionSettings.handleForceRunCancel}
      tags={tags}
      enableConditions
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card sx={{ borderRadius: '12px', overflow: 'hidden' }}>
            <CardHeader
              title={<Typography sx={{ fontWeight: 700 }}>{t('workflow.search_and_insert_image_into_pdf.source_title', 'Source File')}</Typography>}
              sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff' }}
            />
            <CardContent>
              <RadioGroup row value={sourceMode} onChange={handleSourceModeChange}>
                <FormControlLabel value="current" control={<Radio />} label={t('workflow.insert_image_into_pdf.use_current_source', 'Use current event file')} />
                <FormControlLabel value="other" control={<Radio />} label={t('workflow.insert_image_into_pdf.use_custom_source', 'Use custom PDF path')} />
              </RadioGroup>

              {sourceMode === 'other' && (
                <Box sx={{ mt: 2, display: 'grid', gap: 1.5 }}>
                  <TagEditor
                    caseid={caseid}
                    ref={sourcePdfRef}
                    value={sourcePdfPath}
                    onChange={setSourcePdfPath}
                    placeholder={t('workflow.search_and_insert_image_into_pdf.source_placeholder', '/path/to/source.pdf')}
                    singleLine
                    multiline={false}
                    disabled={sourceMode !== 'other'}
                    onTagClick={(event) => handleMenuOpen(event, 'sourcePdf')}
                    tags={tags}
                  />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={handleBrowseSourcePdfClick}
                      disabled={sourceMode !== 'other'}
                      sx={{ textTransform: 'none' }}
                    >
                      {t('workflow.search_and_insert_image_into_pdf.browse_source', 'Browse File')}
                    </Button>
                  </Box>
                  {filePathError && (
                    <Typography variant="body2" color="error">
                      {filePathError}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: '12px', overflow: 'hidden' }}>
            <CardHeader
              title={<Typography sx={{ fontWeight: 700 }}>{t('workflow.insert_image_into_pdf.image_title', 'Image to insert')}</Typography>}
              sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff' }}
            />
            <CardContent>
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                <TagEditor
                  caseid={caseid}
                  ref={imagePathRef}
                  value={imagePath}
                  onChange={setImagePath}
                  placeholder={t('workflow.insert_image_into_pdf.image_placeholder', '/path/to/image.png')}
                  singleLine
                  multiline={false}
                  onTagClick={(event) => handleMenuOpen(event, 'image')}
                  tags={tags}
                />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<ImageIcon />}
                    onClick={handleBrowseImageClick}
                    sx={{ textTransform: 'none' }}
                    >
                      {t('workflow.insert_image_into_pdf.browse_image', 'Browse Image')}
                    </Button>
                  </Box>
                  {imagePathError && (
                    <Typography variant="body2" color="error">
                      {imagePathError}
                    </Typography>
                  )}
                </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: '12px', overflow: 'hidden' }}>
            <CardHeader
              title={<Typography sx={{ fontWeight: 700 }}>{t('workflow.search_and_insert_image_into_pdf.search_title', 'Search Zone')}</Typography>}
              sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff' }}
            />
            <CardContent>
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                <TagEditor
                  caseid={caseid}
                  ref={requestRef}
                  value={request}
                  onChange={setRequest}
                  placeholder={t('workflow.search_and_insert_image_into_pdf.search_placeholder', 'describe the zone to find')}
                  singleLine={false}
                  multiline
                  minHeight="84px"
                  onTagClick={(event) => handleMenuOpen(event, 'request')}
                  tags={tags}
                />
                <Typography variant="body2" color="text.secondary">
                  {requestError || t('workflow.search_and_insert_image_into_pdf.search_help', 'The image will be centered inside the detected zone.')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: '12px', overflow: 'hidden' }}>
            <CardHeader
              title={<Typography sx={{ fontWeight: 700 }}>{t('workflow.insert_image_into_pdf.output_title', 'Output PDF')}</Typography>}
              sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff' }}
            />
            <CardContent>
              <TagEditor
                caseid={caseid}
                ref={targetPathRef}
                value={targetPath}
                onChange={setTargetPath}
                placeholder={t('workflow.insert_image_into_pdf.target_placeholder', '/{filename}_with_image.pdf')}
                singleLine
                multiline={false}
                onTagClick={(event) => handleMenuOpen(event, 'target')}
                tags={tags}
              />
              <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<FolderOpenIcon />}
                  onClick={handleBrowseTargetClick}
                  sx={{ textTransform: 'none' }}
                >
                  {t('workflow.insert_image_into_pdf.browse_output', 'Browse Output Folder')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Accordion
            expanded={advancedOpen}
            onChange={(_event, nextExpanded) => setAdvancedOpen(nextExpanded)}
            disableGutters
            sx={{ borderRadius: '12px', overflow: 'hidden', boxShadow: 'none', border: '1px solid #e9e9ef' }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>
                {t('workflow.search_and_insert_image_into_pdf.advanced_title', 'Advanced')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 2 }}>
              <TextField
                fullWidth
                type="number"
                label={t('workflow.search_and_insert_image_into_pdf.max_zones_label', 'Max zones')}
                value={maxZones}
                onChange={(event) => setMaxZones(event.target.value)}
                inputProps={{ min: 1, step: 1 }}
                error={Boolean(maxZonesError)}
                helperText={maxZonesError || t('workflow.search_and_insert_image_into_pdf.max_zones_help', 'Leave empty to replace every detected zone.')}
              />
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      {SourcePdfBrowserComponent}
      {ImageBrowserComponent}
      {TargetBrowserComponent}
      <TagsDropdown
        tags={tags}
        handleTagSelect={handleTagSelect}
        anchorEl={anchorEl}
        setAnchorEl={handleMenuClose}
        additionalTags={customDataTags}
        menuType={menuType}
      />
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsSearchAndInsertImageIntoPdf);
